import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import StatsCards from './components/StatsCards';
import WeeklyTrendChart from './components/WeeklyTrendChart';
import DistributionCharts from './components/DistributionCharts';
import ConversionFunnel from './components/ConversionFunnel';
import LeadTable from './components/LeadTable';
import VisitorSessionList from './components/VisitorSessionList';
import VisitorStatsBar from './components/VisitorStatsBar';
import FunnelDropoff from './components/FunnelDropoff';
import SectionHeatmap from './components/SectionHeatmap';

interface Lead {
  id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  company: string | null;
  sector: string | null;
  monthly_target: string | null;
  target_audience: string | null;
  action_type: string | null;
  notes: string | null;
  lead_context: string | null;
  session_id: string | null;
  status: string;
}

type TabKey = 'leads' | 'visitors';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('leads');

  const fetchLeads = useCallback(async () => {
    const { data } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setLeads(data as Lead[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate('/admin/login');
        return;
      }
      setUserEmail(session.user.email || '');
      fetchLeads();
    });
  }, [navigate, fetchLeads]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const handleStatusChange = (id: string, status: string) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
  };

  // Build a map of session_id -> lead name for VisitorSessionList
  const leadSessionMap: Record<string, string> = {};
  leads.forEach((l) => {
    if (l.session_id) {
      leadSessionMap[l.session_id] = `${l.first_name} ${l.last_name}`;
    }
  });

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0a0a14 0%, #0f0f1a 100%)' }}
      >
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-3xl animate-spin" style={{ color: '#f0b429' }} />
          <p className="text-gray-500 text-sm">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{ background: 'linear-gradient(135deg, #0a0a14 0%, #0f0f1a 100%)' }}
    >
      {/* Top Nav */}
      <header
        className="sticky top-0 z-40 flex items-center justify-between px-6 py-4"
        style={{ background: 'rgba(10,10,20,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #f0b429, #e67e22)' }}
          >
            <i className="ri-bar-chart-2-line text-black text-sm" />
          </div>
          <div>
            <span className="text-white font-black text-base">Smart<span style={{ color: '#f0b429' }}>Panel</span></span>
            <span className="text-gray-600 text-xs ml-2">Lead Analiz</span>
          </div>
        </div>

        {/* Tab switcher */}
        <div
          className="flex items-center gap-1 p-1 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          {([
            { key: 'leads', label: 'Lead Paneli', icon: 'ri-user-star-line' },
            { key: 'visitors', label: 'Ziyaretçi Analizi', icon: 'ri-eye-line' },
          ] as { key: TabKey; label: string; icon: string }[]).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
              style={{
                background: activeTab === tab.key ? 'rgba(240,180,41,0.2)' : 'transparent',
                color: activeTab === tab.key ? '#f0b429' : '#6b7280',
              }}
            >
              <i className={`${tab.icon} text-xs`} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={fetchLeads}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-500 hover:text-white transition-colors cursor-pointer"
            style={{ background: 'rgba(255,255,255,0.05)' }}
            title="Yenile"
          >
            <i className="ri-refresh-line text-sm" />
          </button>
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(240,180,41,0.15)' }}>
              <i className="ri-user-line text-xs" style={{ color: '#f0b429' }} />
            </div>
            <span className="text-gray-400 text-xs">{userEmail}</span>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            style={{ background: 'rgba(255,255,255,0.05)' }}
          >
            <i className="ri-logout-box-line" />
            Çıkış
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="px-4 md:px-6 py-6 max-w-7xl mx-auto space-y-6">

        {/* ── LEAD PANELİ TAB ── */}
        {activeTab === 'leads' && (
          <>
            <div>
              <h2 className="text-white text-xl font-extrabold">Lead Paneli</h2>
              <p className="text-gray-300 text-sm mt-0.5">WhatsApp formundan gelen tüm lead&apos;leri buradan takip edin</p>
            </div>

            <StatsCards leads={leads} />
            <WeeklyTrendChart leads={leads} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <DistributionCharts leads={leads} />
              </div>
              <div>
                <ConversionFunnel leads={leads} />
              </div>
            </div>

            <div>
              <h3 className="text-white font-bold text-sm mb-4">Tüm Lead&apos;ler ({leads.length})</h3>
              <LeadTable leads={leads} onStatusChange={handleStatusChange} />
            </div>
          </>
        )}

        {/* ── ZİYARETÇİ ANALİZİ TAB ── */}
        {activeTab === 'visitors' && (
          <>
            <div>
              <h2 className="text-white text-xl font-extrabold">Ziyaretçi Analizi</h2>
              <p className="text-gray-300 text-sm mt-0.5">
                Form göndermeyenler dahil tüm ziyaretçilerin davranışlarını izleyin
              </p>
            </div>

            <VisitorStatsBar />

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Funnel — 2 cols */}
              <div className="lg:col-span-2">
                <FunnelDropoff />
              </div>

              {/* Session list — 3 cols */}
              <div
                className="lg:col-span-3 rounded-2xl p-5"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div className="mb-4">
                  <h3 className="text-white font-bold text-sm">Tüm Ziyaretçi Oturumları</h3>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Her satırda yol ikonuna tıklayarak o kişinin tam davranış geçmişini görebilirsiniz
                  </p>
                </div>
                <VisitorSessionList leadSessionMap={leadSessionMap} />
              </div>
            </div>

            {/* Section Heatmap — full width */}
            <SectionHeatmap />

            {/* Journey legend */}
            <div
              className="rounded-2xl p-4"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-3">Yolculuk Adımları Açıklaması</p>
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: 'ri-login-circle-line', label: 'Siteye Girdi', color: '#a78bfa' },
                  { icon: 'ri-arrow-down-double-line', label: '%50 Scroll', color: '#60a5fa' },
                  { icon: 'ri-cursor-line', label: 'Butona Bastı', color: '#00d4ff' },
                  { icon: 'ri-window-2-line', label: 'Form Açtı', color: '#f0b429' },
                  { icon: 'ri-arrow-right-circle-line', label: 'Adım 2\'ye Geçti', color: '#fb923c' },
                  { icon: 'ri-whatsapp-line', label: 'Lead Oldu', color: '#4ade80' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-1.5">
                    <div
                      className="w-5 h-5 flex items-center justify-center rounded-full"
                      style={{ background: `${item.color}18`, border: `1px solid ${item.color}30` }}
                    >
                      <i className={`${item.icon} text-[10px]`} style={{ color: item.color }} />
                    </div>
                    <span className="text-gray-500 text-xs">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
